import { creatorbase, creatorbaseQueries, applicationManager, queryHelpers } from '@round/api';
import { GenericDropdownOption } from 'App.types';
import { useInfiniteQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import uniq from 'lodash/uniq';

const projectQueryOptions = creatorbaseQueries.project.options.infinite;

export type ProjectCampaignOption = GenericDropdownOption<number> & creatorbase.Campaign;

export type ProjectCampaignOptionGroup = creatorbase.Project & {
    label: string;
    brand: creatorbase.Brand | null;
    team: creatorbase.Team | null;
    options: readonly ProjectCampaignOption[];
};

type Params = Omit<creatorbase.GetProjectsParams, 'page_size' | 'page'> & {
    platform?: creatorbase.Platform;
    managed_by?: applicationManager.Application;
};

type Config = {
    enabled?: boolean;
};

export function useProjectCampaignGroups(
    { platform, managed_by, ...projectQueryParams }: Params,
    config: Config = { enabled: true }
) {
    const queryClient = useQueryClient();

    const query = useInfiniteQuery({
        ...projectQueryOptions({ ...projectQueryParams, page_size: 25 }),
        enabled: config.enabled,
        placeholderData: (data) => data,
    });

    const brandQueries = useQueries({
        queries:
            query.data?.pages.map<ReturnType<typeof creatorbaseQueries.brand.options.list>>((page) => {
                const brandIds = uniq(page.results.map((p) => p.brand_id));
                const page_size = brandIds.length;
                return {
                    ...creatorbaseQueries.brand.options.list({ id: brandIds.toString(), page_size }),
                    enabled: config.enabled && brandIds.length > 0,
                    placeholderData: (data) => data,
                };
            }) ?? [],
    });

    const teamQueries = useQueries({
        queries:
            query.data?.pages.map<ReturnType<typeof creatorbaseQueries.team.options.list>>((page) => {
                const teamIds = uniq(page.results.map((p) => p.team_id));
                const page_size = teamIds.length;
                return {
                    ...creatorbaseQueries.team.options.list({ id: teamIds.toString(), page_size }),
                    enabled: config.enabled && teamIds.length > 0,
                    placeholderData: (data) => data,
                };
            }) ?? [],
    });

    const campaignQueries = useQueries({
        queries:
            query.data?.pages.flatMap((page) => {
                const projectIds = page.results.map((p) => p.id);
                return projectIds.map<ReturnType<typeof creatorbaseQueries.campaigns.options.list<undefined>>>(
                    (projectId) => ({
                        ...creatorbaseQueries.campaigns.options.list({
                            project_id: projectId,
                            page_size: 100,
                            platform: platform,
                            managed_by: managed_by,
                        }),
                        enabled: config.enabled,
                        placeholderData: (data) => data,
                    })
                );
            }) ?? [],
    });

    const groups: ProjectCampaignOptionGroup[] =
        query.data?.pages.flatMap((page, pageIndex) => {
            const brandsResult = brandQueries[pageIndex];
            const teamsResult = teamQueries[pageIndex];

            const pageProjectIds = page.results.map((p) => p.id);
            // Check if campaigns data is pending for the current page, (fetching and no previous data).
            // We will return from this page's options mapping if so, to prevent showing project group
            // options which have no campaigns due to an ongoing fetch.
            const pageCampaignsFetching = queryClient.isFetching({
                queryKey: creatorbaseQueries.campaigns.keys.lists(),
                predicate: (q) => {
                    const { params: campaignParams } = queryHelpers.parseQueryKey(
                        q.queryKey as queryHelpers.QueryKey<creatorbase.GetCampaignsParams>
                    );
                    return Boolean(
                        campaignParams.project_id && pageProjectIds.includes(campaignParams.project_id) && !q.state.data
                    );
                },
            });

            if (!brandsResult?.isSuccess || !teamsResult?.isSuccess || pageCampaignsFetching > 0) {
                return [];
            }

            return page.results.map((project) => {
                const projectCampaignQuery = campaignQueries.find((q) => {
                    const projectId = q.data?.results?.[0]?.project_id;
                    return projectId === project.id;
                });

                const campaignOptions: ProjectCampaignOption[] =
                    projectCampaignQuery?.data?.results?.map((c) => ({
                        ...c,
                        value: c.id,
                        label: c.name,
                    })) ?? [];

                return {
                    ...project,
                    label: project.name,
                    brand: brandsResult.data?.results.find((b) => b.id === project.brand_id) ?? null,
                    team: teamsResult.data?.results.find((t) => t.id === project.team_id) ?? null,
                    options: campaignOptions,
                };
            });
        }) ?? [];

    const isLoading = query.isLoading || campaignQueries.some((r) => r.isFetching);

    const fetchNextPage = () => {
        if (query.hasNextPage && !query.isFetching) {
            query.fetchNextPage();
        }
    };

    return {
        groups,
        isLoading,
        fetchNextPage,
        hasNextPage: query.hasNextPage ?? false,
        isFetchingNextPage: query.isFetchingNextPage,
        isFetching: query.isFetching,
        error: query.error,
    };
}
